/**
 * Helper functions for navigation mapping
 */

/**
 * Recursively searches an object tree to find an item by name
 * @param {Object} obj - Object tree to search
 * @param {string} name - Name to search for
 * @return {Object|null} Found item or null
 */
import { sanitizeText } from "@/utils/textUtils"
import { modifyHref } from "@/components/Shared/HeaderNavTabs/utils"
import _ from "lodash"
import {removeAndCleanLink}from "@/utils/helper"

export const findItemByName = (obj, name) => {
  // Check if obj is null/undefined first
  if (!obj) return {}
  // If we have :items, check them first
  if (obj[":items"]) {
    // Look through each key in :items
    for (const key of Object.keys(obj[":items"])) {
      // If key contains our search name
      if (key.includes(name)) {
        return obj[":items"][key]
      }
      // If this item has nested :items, recurse into them
      const result = findItemByName(obj[":items"][key], name)
      if (result) return result
    }
  }
  // Return empty object instead of null to allow safe property access
  return null
}

export const hasHeaderItems = header => {
  if (header[":items"]) {
    for (const key of Object.keys(header[":items"])) {
      if (
        header[":items"][key][":items"] &&
        Object.keys(header[":items"][key][":items"]).length > 0
      ) {
        return true
      }
    }
  }
  return false
}

/**
 * Extracts menu items from a navigation object
 * @param {Object} obj - Navigation object
 * @return {Array} Array of menu items
 */
export function getMenuItems(obj) {
  const result = []
  // Check if object has itemsOrder
  if (obj === null || !obj[":itemsOrder"]) return result

  // Iterate through itemsOrder
  for (const itemKey of obj[":itemsOrder"]) {
    const item = obj[":items"][itemKey]

    // For each item, check its :items
    // if (item[":items"]) {
      // Look for keys containing globalnavigation_v3
      for (const key of Object.keys(item?.[":items"])) {
        if (key.includes("globalnavigation")) {
          result.push({
            ...item[":items"][key],
            appliedCssClassNames: item.appliedCssClassNames ?? "",
          })
        }
        else{
          result.push({})         
        }
      }
    }

  return result
}

const checkForBusinessLevelOneArray = obj => {
  if (!obj?.levelOneNavMulti) return false

  return obj.levelOneNavMulti.some(levelOne => {
    if (!levelOne?.levelTwoNavMulti) return false

    // Check if all levelTwo items have empty levelThreeNavMulti arrays
    return levelOne.levelTwoNavMulti.every(levelTwo => {
      return levelTwo?.levelThreeNavMulti?.length === 0
    })
  })
}

export const transformArray = (obj, tabTitle) => {
  const res = []
  obj["levelOneNavMulti"]?.forEach(item => {
    const levelTwoArray = []
    item?.levelTwoNavMulti?.forEach(levelTwo => {
      levelTwoArray.push({
        title: {
          trackingAttributes: {
            "data-datalayer-id": "kds-element-navigation",
            "data-datalayer-data": JSON.stringify({
              clickInternalLinks: modifyHref(levelTwo.groupLink)
                ? "true"
                : "false",
              internalLinkName: `${tabTitle} > ${sanitizeText(
                levelTwo.groupTitle
              )}`.toLowerCase(),
              internalLinkURL: levelTwo.groupLink
                ? `${
                    levelTwo.groupLink.indexOf("http") === -1
                      ? typeof window !== "undefined" && window.location.origin
                      : ""
                  }${modifyHref(levelTwo.groupLink)}`?.toLowerCase()
                : "n/a",
            }),
          },
          text: sanitizeText(levelTwo.groupTitle),
          href: modifyHref(levelTwo.groupLink),
          target: levelTwo.newTabGroup ? "_blank" : "_self",
        },
        isCTA: levelTwo.groupTitle?.includes("carrot-icon"),

        items:
          levelTwo.levelThreeNavMulti?.length > 0
            ? levelTwo.levelThreeNavMulti?.map(levelThree => {
                return {
                  trackingAttributes: {
                    "data-datalayer-id": "kds-element-navigation",

                    "data-datalayer-data": JSON.stringify({
                      clickInternalLinks: modifyHref(levelThree.link)
                        ? "true"
                        : "false",
                      internalLinkName: `${tabTitle} > ${sanitizeText(
                        levelTwo.groupTitle
                      )} > ${sanitizeText(levelThree.label)}`?.toLowerCase(),
                      internalLinkURL: levelThree.link
                        ? `${
                            levelThree.link.indexOf("http") === -1
                              ? typeof window !== "undefined" &&
                                window.location.origin
                              : ""
                          }${modifyHref(levelThree.link)}`.toLowerCase()
                        : "n/a",
                    }),
                  },
                  text: sanitizeText(levelThree.label),
                  href: modifyHref(levelThree.link),
                  target: levelThree.newTab ? "_blank" : "_self",
                }
              })
            : undefined,
      })
    })

    res.push(levelTwoArray)
  })
  return res
}

export const transformBusinessArray = (obj, tabTitle) => {
  const res = []
  obj["levelOneNavMulti"]?.forEach(item => {
    // Check if this levelOne item has all empty levelThreeNavMulti arrays
    if (checkForBusinessLevelOneArray({ levelOneNavMulti: [item] })) {
      // If true, create a simplified structure with just titles and links
      const simplifiedStructure = {
        items:
          item.levelTwoNavMulti?.map(levelTwo => ({
            title: {
              text: sanitizeText(levelTwo.groupTitle),
              href: modifyHref(levelTwo.groupLink),
              trackingAttributes: {
                "data-datalayer-id": "kds-element-navigation",
                "data-datalayer-data": JSON.stringify({
                  clickInternalLinks: modifyHref(levelTwo.groupLink)
                    ? "true"
                    : "false",
                  internalLinkName: `${tabTitle} > ${sanitizeText(
                    levelTwo.groupTitle
                  )}`?.toLowerCase(),
                  internalLinkURL: levelTwo.groupLink
                    ? `${
                        levelTwo.groupLink.indexOf("http") === -1
                          ? typeof window !== "undefined" &&
                            window.location.origin
                          : ""
                      }${modifyHref(levelTwo.groupLink)}`?.toLowerCase()
                    : "n/a",
                }),
              },
            },
            newTabGroup: levelTwo.newTabGroup,
          })) || [],
      }

      res.push(simplifiedStructure)
    } else {
      // If false, keep the existing structure with tracking attributes
      const levelTwoArray = []
      item?.levelTwoNavMulti?.forEach(levelTwo => {
        levelTwoArray.push({
          title: {
            text: sanitizeText(levelTwo.groupTitle),
            href: modifyHref(levelTwo.groupLink),
            target: levelTwo.newTabGroup ? "_blank" : "_self",
            trackingAttributes: {
              "data-datalayer-id": "kds-element-navigation",
              "data-datalayer-data": JSON.stringify({
                clickInternalLinks: modifyHref(levelTwo.groupLink)
                  ? "true"
                  : "false",
                internalLinkName: `${tabTitle} > ${sanitizeText(
                  levelTwo.groupTitle
                )}`?.toLowerCase(),
                internalLinkURL: levelTwo.groupLink
                  ? `${
                      levelTwo.groupLink.indexOf("http") === -1
                        ? typeof window !== "undefined" &&
                          window.location.origin
                        : ""
                    }${modifyHref(levelTwo.groupLink)}`?.toLowerCase()
                  : "n/a",
              }),
            },
          },
          items:
            levelTwo.levelThreeNavMulti?.length > 0
              ? levelTwo.levelThreeNavMulti?.map(levelThree => ({
                  text: sanitizeText(levelThree.label),
                  href: modifyHref(levelThree.link),
                  target: levelThree.newTab ? "_blank" : "_self",
                  trackingAttributes: {
                    "data-datalayer-id": "kds-element-navigation",

                    "data-datalayer-data": JSON.stringify({
                      clickInternalLinks: modifyHref(levelThree.link)
                        ? "true"
                        : "false",
                      internalLinkName: `${tabTitle} > ${sanitizeText(
                        levelTwo.groupTitle
                      )} > ${sanitizeText(levelThree.label)}`.toLowerCase(),
                      internalLinkURL: levelThree.link
                        ? `${
                            levelThree.link.indexOf("http") === -1
                              ? typeof window !== "undefined" &&
                                window.location.origin
                              : ""
                          }${modifyHref(levelThree.link)}`?.toLowerCase()
                        : "n/a",
                    }),
                  },
                }))
              : undefined,
        })
      })
      res.push(levelTwoArray)
    }
  })
  return res
}

/**
 * Gets panel titles from a navigation object
 * @param {Object} obj - Navigation object
 * @return {Array<string>} Array of panel titles
 */
export function getPanelTitles(obj) {  
  // Check if object has required properties
  if (obj === null || !obj[":itemsOrder"] || !obj[":items"]) return []
  
  // Get an array of titles
  const titles = obj[":itemsOrder"].map(key => {
    const item = obj[":items"][key]
    return item["cq:panelTitle"] || ""
  })

  return titles
}


//new function - non stock nav
export function getTabTitles(obj) {
  
  // Check if object has required properties
  if (obj === null || !obj[":itemsOrder"] || !obj[":items"]) return []
  

  // Get an array of titles
  const titles = obj[":itemsOrder"].map(key => {
    const item = obj[":items"][key]
      return item["cq:panelTitle"] || ""
  })
  return titles
}


export function getTabDetails(obj) {
  // Check if object has required properties
  if (obj === null || !obj[":itemsOrder"] || !obj[":items"]) return [];

  // Get all menu items once
  const tabMenuItems = getMenuItems(obj);

  // Get an array of tab detail objects
  const titles = obj[":itemsOrder"].map((key, index) => {
    const item = obj[":items"][key];

    // Find tabs and recursively get tab details if they exist
    const tabsObj = findItemByName(item, "tabs");
    const tabTitles = tabsObj && tabsObj[":itemsOrder"] ? getTabDetails(tabsObj) : [];

    // Get the menu item for this index if it exists
    const menuItem = tabMenuItems[index];

    // Get imageOrientation from menuItem (from AEM JSON)
    const imageOrientation = menuItem?.imageOrientation || "portrait";

    // Transform the menu item if it exists
    const menuItemTransformed = menuItem
      ? transformArray(menuItem, menuItem["cq:panelTitle"])
      : [];

    // Build images array with null checks
    const tabTitle = item["cq:panelTitle"] || "";
    const images = [];
    if (menuItem?.firstImagesblock?.firstImageFileReference) {
      images.push({
        image: {
          src: menuItem.firstImagesblock.firstImageFileReference || "",
          alt: menuItem.firstImagesblock.altText || "",
          "data-mode": "smartcrop",
          trackingAttributes: {
            "data-datalayer-id": "kds-element-navigation",
            "data-datalayer-data": JSON.stringify({
              clickInternalLinks: modifyHref(menuItem.firstImagesblock.ctaLink) ? "true" : "false",
              internalLinkName: `${tabTitle} > ${sanitizeText(menuItem?.firstImagesblock?.firstImageTitle || "")} > img_${menuItem?.firstImagesblock?.ctaLabel || ""}`.toLowerCase(),
              internalLinkURL: modifyHref(menuItem.firstImagesblock.ctaLink)
                ? `${
                    modifyHref(menuItem.firstImagesblock.ctaLink)?.indexOf("http") === -1
                      ? typeof window !== "undefined" && window.location.origin
                      : ""
                  }${modifyHref(menuItem.firstImagesblock.ctaLink)}`
                : "n/a",
            }),
          },
        },
        imageOrientation,
        caption: {
          text: menuItem?.firstImagesblock?.firstImageTitle
            ? sanitizeText(menuItem.firstImagesblock.firstImageTitle)
            : "",
        },
        multiCTA: Array.isArray(menuItem?.multiCTA)
          ? menuItem.multiCTA.map((elem) => ({
              text: sanitizeText(elem?.ctaLabel || ""),
              href: modifyHref(elem?.ctaLink),
              trackingAttributes: {
                "data-datalayer-id": "kds-element-navigation",
                "data-datalayer-data": JSON.stringify({
                  clickInternalLinks: modifyHref(menuItem.firstImagesblock.ctaLink) ? "true" : "false",
                  internalLinkName: `${tabTitle} > ${sanitizeText(elem?.ctaLabel || "")}`.toLowerCase(),
                  internalLinkURL: modifyHref(elem?.ctaLink)
                    ? `${
                        modifyHref(elem.ctaLink)?.indexOf("http") === -1
                          ? typeof window !== "undefined" && window.location.origin
                          : ""
                      }${modifyHref(elem.ctaLink)}`
                    : "n/a",
                }),
              },
            }))
          : [],
        CTA: menuItem?.firstImagesblock?.ctaLabel
          ? {
              text: menuItem.firstImagesblock.ctaLabel,
              href: modifyHref(menuItem.firstImagesblock.ctaLink),
              target: menuItem.firstImagesblock.ctaNewTab ? '_blank' : undefined,
              trackingAttributes: {
                "data-datalayer-id": "kds-element-navigation",
                "data-datalayer-data": JSON.stringify({
                  clickInternalLinks: modifyHref(menuItem.firstImagesblock.ctaLink) ? "true" : "false",
                  internalLinkName: `${tabTitle} > ${sanitizeText(menuItem?.firstImagesblock?.firstImageTitle || "")} > img_${menuItem?.firstImagesblock?.ctaLabel || ""}`.toLowerCase(),
                  internalLinkURL: modifyHref(menuItem.firstImagesblock.ctaLink)
                    ? `${
                        modifyHref(menuItem.firstImagesblock.ctaLink)?.indexOf("http") === -1
                          ? typeof window !== "undefined" && window.location.origin
                          : ""
                      }${modifyHref(menuItem.firstImagesblock.ctaLink)}`
                    : "n/a",
                }),
              },
            }
          : undefined,
      });
    }
    if (menuItem?.secondImagesblock?.secondImageFileReference) {
      images.push({
        image: {
          src: menuItem.secondImagesblock.secondImageFileReference || "",
          alt: menuItem.secondImagesblock.altText || "",
          "data-mode": "smartcrop",
          trackingAttributes: {
            "data-datalayer-id": "kds-element-navigation",
            "data-datalayer-data": JSON.stringify({
              clickInternalLinks: modifyHref(menuItem.secondImagesblock.ctaLink) ? "true" : "false",
              internalLinkName: `${tabTitle} > ${sanitizeText(menuItem?.secondImagesblock?.secondImageTitle || "")} > img_${menuItem?.secondImagesblock?.ctaLabel || ""}`.toLowerCase(),
              internalLinkURL: modifyHref(menuItem.secondImagesblock.ctaLink)
                ? `${
                    modifyHref(menuItem.secondImagesblock.ctaLink)?.indexOf("http") === -1
                      ? typeof window !== "undefined" && window.location.origin
                      : ""
                  }${modifyHref(menuItem.secondImagesblock.ctaLink)}`
                : "n/a",
            }),
          },
        },
        imageOrientation,
        caption: {
          text: menuItem?.secondImagesblock?.secondImageTitle
            ? sanitizeText(menuItem.secondImagesblock.secondImageTitle)
            : "",
        },
        CTA: menuItem?.secondImagesblock?.ctaLabel
          ? {
              text: menuItem.secondImagesblock.ctaLabel,
              href: modifyHref(menuItem.secondImagesblock.ctaLink),
              trackingAttributes: {
                "data-datalayer-id": "kds-element-navigation",
                "data-datalayer-data": JSON.stringify({
                  clickInternalLinks: modifyHref(menuItem.secondImagesblock.ctaLink) ? "true" : "false",
                  internalLinkName: `${tabTitle} > ${sanitizeText(menuItem?.secondImagesblock?.secondImageTitle || "")} > img_${menuItem?.secondImagesblock?.ctaLabel || ""}`.toLowerCase(),
                  internalLinkURL: modifyHref(menuItem.secondImagesblock.ctaLink)
                    ? `${
                        modifyHref(menuItem.secondImagesblock.ctaLink)?.indexOf("http") === -1
                          ? typeof window !== "undefined" && window.location.origin
                          : ""
                      }${modifyHref(menuItem.secondImagesblock.ctaLink)}`
                    : "n/a",
                }),
              },
            }
          : undefined,
      });
    }

    return {
      title: item["cq:panelTitle"] || "",
      tabs: tabTitles,
      menuItems: menuItemTransformed,
      images,
      parentLink: removeAndCleanLink(menuItem?.parentLink),
    };
  });

  return titles;
}
